import { MAuswahlwert, MDetailSerie, MSerie } from "../../../metamodell";
import { PAuswahlwert, PDetailSerie, PSerie } from "../../../metamodell_paths";
import * as Commons from "oc-commons/Commons";
import { ZElement } from "../../arrangement/ZElement";
import { useEffect, useRef, useState } from "react";
import { getBlobDataPromise, getRendererCanvas, getVariantUniqueId } from "./ImageCreator";
import {
  filterPathIntersectionCached,
  following_are_optionals,
} from "oc-app/metadata/GraphMetadata";
import { elementOrTor } from "../../arrangement/elementFunctions";

const tableName = "ElementThumbnails";
type InternalPair = { füllung: MAuswahlwert; mass: MAuswahlwert };

function getFüllungMassPairs(): InternalPair[] {
  const auswahlwerte: MAuswahlwert[] = APP.metadata.Auswahlwert;
  const füllungAuswahlwerte = auswahlwerte.filter((a) => a.Auswahl.Key == "Füllung" && !a.Gesperrt);
  const pairs = new Map<string, InternalPair>();
  füllungAuswahlwerte.forEach((füllung) => {
    const detailserie = filterPathIntersectionCached("SnapshooterfüllungDetailserie")(
      füllung,
      (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).DetailSerie
    ) as MDetailSerie[];
    if (detailserie.some((d) => d.ElementeEinzelnListen)) {
      const massAuswahlwerte: MAuswahlwert[] = filterPathIntersectionCached(
        "SnapshooterFüllungMass"
      )(füllung, (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).Mass.EasyAuswahlwert);
      massAuswahlwerte.forEach((mass) => {
        pairs.set(`${mass.Key}|${füllung.Key}`, { mass, füllung });
      });
    } else {
      const massAuswahlwerte = filterPathIntersectionCached("SnapshooterDetailserieMass")(
        detailserie,
        (p: PDetailSerie) => elementOrTor(p.Serie.DetailSerie).Mass.EasyAuswahlwert
      ) as MAuswahlwert[];
      massAuswahlwerte.forEach((mass) => {
        pairs.set(`${mass.Key}|${füllung.Key}`, { mass, füllung });
      });
    }
  });
  return [...pairs.values()].filter(({ mass, füllung }) => !mass.Gesperrt && !füllung.Gesperrt);
}

const toElementThumbnail = (internal: InternalPair, filename = "") => {
  const oldId = APP.metadata.ElementThumbnails.find(
    ({ Füllung, Mass }) => Füllung == internal.füllung && Mass == internal.mass
  )?.id;
  return {
    Füllung: internal.füllung.Key,
    Mass: internal.mass.Key,
    Thumbnail: filename,
    id: oldId,
  };
};

function createArrangementClone() {
  const agtClone = APP.arrangement.clone();
  // ist ok, weil dieser clone eh' nur einmal verwendet wird
  agtClone.ghostPool.start();
  return agtClone;
}

async function runUploads(pair: InternalPair): Promise<boolean> {
  let element: ZElement;
  const objectFromPair = {
    massAuswahlwert: pair.mass,
    füllungAuswahlwert: pair.füllung,
  };
  try {
    const clone = createArrangementClone();
    clone.serie = (
      filterPathIntersectionCached("ElementSnapshoter.tsx:47")(
        pair.mass,
        (p: PAuswahlwert) => elementOrTor(p.Mass).DetailSerie.Serie,
        pair.füllung,
        (p: PAuswahlwert) => p.Inspiration.DetailSerie.Serie,
        following_are_optionals,
        APP.arrangement.serie,
        (p: PSerie) => p
      ) as MSerie[]
    )[0];

    element = clone.createElement(objectFromPair);
    element.updateMetas();
    if (element.füllungAuswahlwert != pair.füllung) {
      return false;
    }
  } catch (e) {
    console.error(e);
    return false;
  }
  const imagedata = await getBlobDataPromise(element);
  const asciiFilename = (filename: string) => filename.replace(/[^a-zA-Z0-9_\-.]/g, "");

  const file = {
    fileName: asciiFilename(`${getVariantUniqueId(objectFromPair)}_${new Date().getTime()}.png`),
    blob: imagedata,
  };

  const newMeta = toElementThumbnail(pair, file.fileName);
  await Promise.all([
    APP.wkfgBackendApi.saveMetadata(tableName, newMeta.id != null ? "update" : "create", newMeta),
    APP.wkfgBackendApi.uploadContent(tableName, [file]),
  ]);
  return true;
}

export function ElementSnapshoter() {
  const pairs = getFüllungMassPairs();
  const [uploadCount, setUploadCount] = useState(0);
  const [selectedPairs, setSelectedPairs] = useState(pairs);
  const [selectedMass, setSelectedMass] = useState<MAuswahlwert>();
  const [selectedFüllung, setSelectedFüllung] = useState<MAuswahlwert>();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadProtokoll, setUploadProtokoll] = useState<
    (InternalPair & { hochgeladen: boolean })[]
  >([]);
  const previewRef = useRef<HTMLDivElement>();
  useEffect(() => {
    previewRef.current?.replaceChildren(getRendererCanvas());
  }, [previewRef.current]);
  useEffect(() => {
    setSelectedPairs(
      pairs.filter(
        (p) =>
          (selectedMass == null || selectedMass == p.mass) &&
          (selectedFüllung == null || selectedFüllung == p.füllung)
      )
    );
  }, [selectedMass, selectedFüllung]);

  const massWerte = [...new Set(pairs.map((p) => p.mass))].sort((a, b) =>
    a.Name < b.Name ? -1 : 1
  );
  const füllungWerte = [...new Set(pairs.map((p) => p.füllung))].sort((a, b) =>
    a.Name < b.Name ? -1 : 1
  );
  const updateAllSnapshots = async () => {
    setUploading(true);
    setUploadProtokoll([]);
    for (let i = 0; i < selectedPairs.length; i++) {
      setUploadCount(i);
      const hochgeladen = await runUploads(selectedPairs[i]);
      await Commons.sleep(0); //erlaube der UI sich auch im Fehlerfall zu aktualisieren
      setUploadProtokoll((p) => [...p, { ...selectedPairs[i], hochgeladen }]);
    }
    setUploading(false);
  };

  const clearTable = async () => {
    setDeleting(true);
    await APP.wkfgBackendApi.truncateMetadata(tableName);
    setDeleting(false);
  };

  return (
    <section aria-labelledby="snapshooter-title">
      <h2 id="snapshooter-title" className="sr-only">
        {I18N("aria::Element-Snapshot-Verwaltung")}
      </h2>
      {uploading ? (
        <>
          <p role="status" aria-live="polite">
            Aktualisiere ${uploadCount}/${selectedPairs.length}
          </p>
          <div
            id={"snapshot_preview"}
            ref={previewRef}
            role="img"
            aria-label={I18N("aria::Vorschau des aktuellen Snapshots")}
          ></div>
        </>
      ) : deleting ? (
        <p role="status" aria-live="polite">
          {" "}
          {I18N("aria::Lösche alle Einträge und Thumbnails")}
        </p>
      ) : (
        <>
          <table>
            <tbody>
              <tr>
                <th scope="row">{I18N("aria::EasyMass-Auswahl")}</th>
                <td>
                  <label htmlFor="mass-select" className="sr-only">
                    {I18N("aria::EasyMass-Auswahl")}
                  </label>
                  <select
                    id="mass-select"
                    value={selectedMass?.Key ?? ""}
                    onChange={(ev) =>
                      setSelectedMass(massWerte.find((m) => m.Key == ev.target.value))
                    }
                    aria-label={I18N("aria::EasyMass-Auswahl")}
                  >
                    <option value="">{I18N("Alle")}</option>
                    {massWerte.map((m) => (
                      <option key={m.Key} value={m.Key}>
                        {m.Name} ({m.Key})
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th scope="row">{I18N("aria::Füllung-Auswahl")}</th>
                <td>
                  <label htmlFor="fuellung-select" className="sr-only">
                    {I18N("aria::Füllung-Auswahl")}
                  </label>
                  <select
                    id="fuellung-select"
                    value={selectedFüllung?.Key ?? ""}
                    onChange={(ev) =>
                      setSelectedFüllung(füllungWerte.find((m) => m.Key == ev.target.value))
                    }
                    aria-label={I18N("aria::Füllung-Auswahl")}
                  >
                    <option value="">{I18N("Alle")}</option>
                    {füllungWerte.map((m) => (
                      <option key={m.Key} value={m.Key}>
                        {m.Name} ({m.Key})
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
          <p>{selectedPairs.length.toString() + I18N("aria::Kombinationen gefunden")}</p>
          <div role="group" aria-label={I18N("aria::Snapshot-Aktionen")}>
            <button
              onClick={updateAllSnapshots}
              className={"button"}
              disabled={uploading || deleting}
              aria-label={I18N("aria::Aktualisiere Snapshots")}
            >
              {I18N("aria::Aktualisiere Snapshots")}
            </button>
            <button
              onClick={clearTable}
              className={"button"}
              disabled={uploading || deleting}
              aria-label={I18N("aria::Leere Tabelle")}
            >
              {I18N("aria::Leere Tabelle")}
            </button>
          </div>
        </>
      )}
      {uploadProtokoll.length > 0 && (
        <div
          className={"list-input flex-column"}
          role="region"
          aria-label={I18N("aria::Upload-Protokoll")}
        >
          <h3 id="upload-protokoll-title">{I18N("aria::Upload-Protokoll")}</h3>
          <div
            className={"list-input-list uploadProtokoll"}
            aria-labelledby="upload-protokoll-title"
          >
            {uploadProtokoll.map(({ füllung, mass, hochgeladen }, index) => (
              <div key={index} role="listitem">
                <span className={"column"}> {füllung.Key} </span>
                <span className={"column"}> {mass.Key} </span>
                <span className={"column"}>
                  {" "}
                  {hochgeladen ? I18N("aktualisiert") : I18N("verworfen")}{" "}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p role="note">
        {I18N("aria::Alle Änderungen kommen nach einem Reload der Seite zum tragen")}
      </p>
    </section>
  );
}
